/**
 * J6 — die drei Gruppen des Dokumente-Reiters, rein und testbar.
 *
 *  1. Anhänge DIESER Mail (aus dem State des Mailinhalts durchgereicht — kein
 *     zweiter Ladepfad zu Graph),
 *  2. diese Mail in der dauerhaften Ablage (Cluster ABLAGE; solange es die
 *     nicht gibt, entfällt die Gruppe ersatzlos statt leer dazustehen),
 *  3. Dokumente des zugewiesenen Kontakts.
 */

/** A7 — Kategorie der Massen-Altdaten; in der Leiste standardmäßig ausgeblendet. */
export const DOK_KATEGORIE_ALTE_KUNDENAKTE = "Alte Kundenakte";

export type DokZeile = {
    /** Stabil — React-Key und Dedupe. */
    key: string;
    art: "anhang" | "ablage" | "kontakt";
    name: string;
    groesse?: number | null;
    datum?: string | null;
    /** KMVS-Dokument (Viewer/Download). */
    documentId?: number;
    /** Graph-Anhang. */
    messageId?: string;
    attachmentId?: string;
    /** Anhang liegt bereits beim Kontakt. */
    gespeichert?: boolean;
    /** Nur `art='ablage'`: Zustand der dauerhaften Sicherung. */
    zustand?: "pending" | "stored" | "failed" | "zu-gross";
};

export type DokGruppe = { titel: string; zeilen: DokZeile[]; hinweis?: string };

export type AnhangEingabe = {
    messageId: string;
    id: string;
    name: string;
    size?: number;
};

export type AblageEingabe = {
    documentId: number | null;
    fileName: string | null;
    sizeBytes: number | null;
    status: string;
};

export type KontaktDokumentEingabe = {
    id: number;
    fileName: string;
    fileSize?: number | null;
    uploadedAt?: string | null;
};

const passt = (name: string, suche: string): boolean =>
    !suche.trim() || name.toLowerCase().includes(suche.trim().toLowerCase());

/** Zustand einer Ablage-Zeile aus dem Status-String des Backends. */
export function ablageZustand(status: string | null | undefined): DokZeile["zustand"] {
    const s = String(status ?? "").trim().toLowerCase();
    if (s === "stored" || s === "ok") return "stored";
    if (s === "failed" || s === "error") return "failed";
    // E11: zu große Mails werden bewusst NICHT abgelegt — das muss sichtbar
    // sein, nicht still verschluckt.
    if (s === "too-large" || s === "zu-gross" || s === "skipped-too-large") return "zu-gross";
    return "pending";
}

export function baueDokumentGruppen(input: {
    anhaenge: readonly AnhangEingabe[];
    gespeicherteKeys: ReadonlySet<string>;
    /** `null` ⇒ Cluster ABLAGE liefert (noch) nichts ⇒ Gruppe entfällt. */
    ablage: readonly AblageEingabe[] | null;
    kontaktDokumente: readonly KontaktDokumentEingabe[];
    kontaktName: string | null;
    suche: string;
}): DokGruppe[] {
    const out: DokGruppe[] = [];

    const anhaenge: DokZeile[] = (input.anhaenge ?? [])
        .filter((a) => passt(a.name ?? "", input.suche))
        .map((a) => ({
            key: `anhang:${a.messageId}:${a.id}`,
            art: "anhang" as const,
            name: a.name,
            groesse: a.size ?? null,
            messageId: a.messageId,
            attachmentId: a.id,
            gespeichert: input.gespeicherteKeys.has(`${a.messageId}:${a.id}`),
        }));
    if (anhaenge.length > 0) out.push({ titel: "Anhänge dieser Mail", zeilen: anhaenge });

    if (input.ablage !== null) {
        const zeilen: DokZeile[] = (input.ablage ?? [])
            .filter((a) => passt(a.fileName ?? "", input.suche))
            .map((a, i) => ({
                key: `ablage:${a.documentId ?? i}`,
                art: "ablage" as const,
                name: a.fileName ?? "Diese Mail (.eml)",
                groesse: a.sizeBytes,
                documentId: a.documentId ?? undefined,
                zustand: ablageZustand(a.status),
            }));
        if (zeilen.length > 0) out.push({ titel: "Diese Mail in der Ablage", zeilen });
    }

    // Ein Dokument, das schon als Anhang dieser Mail dasteht, wird unten nicht
    // erneut gezeigt — die Anhang-Übernahme legt exakt diese Datei beim Kontakt
    // ab (Dedupe über Name UND Größe, damit gleichnamige Dateien mit anderer
    // Größe erhalten bleiben).
    const anhangSignaturen = new Set(
        (input.anhaenge ?? []).map((a) => `${(a.name ?? "").toLowerCase()}|${a.size ?? ""}`),
    );
    const kontaktZeilen: DokZeile[] = (input.kontaktDokumente ?? [])
        .filter((d) => !anhangSignaturen.has(`${(d.fileName ?? "").toLowerCase()}|${d.fileSize ?? ""}`))
        .map((d) => ({
            key: `kontakt:${d.id}`,
            art: "kontakt" as const,
            name: d.fileName,
            groesse: d.fileSize ?? null,
            datum: d.uploadedAt ?? null,
            documentId: d.id,
        }));
    if (kontaktZeilen.length > 0) {
        out.push({
            titel: input.kontaktName?.trim()
                ? `Dokumente von ${input.kontaktName.trim()}`
                : "Dokumente des Kontakts",
            zeilen: kontaktZeilen,
        });
    }

    return out;
}
